/**
 * MediaPicker — routes media library selections to the right handler.
 *
 * Instead of scattered boolean flags (_addImageMode, _replaceImageMode, etc.),
 * callers declare named actions and open the dialog with an explicit intent.
 *
 * Usage:
 *   const picker = new MediaPicker(dialogElement);
 *   picker.on("fabric:add",     (cm) => { ... });
 *   picker.on("segment:replace",(cm, ctx) => { ... });
 *
 *   // Later, when user clicks a button:
 *   picker.open("fabric:add");
 *   picker.open("segment:replace", { index: 3 });
 *
 *   // Cleanup:
 *   picker.dispose();
 */
export class MediaPicker {
  /**
   * @param {HTMLElement} dialog - the dialog element that emits "content_medium:selected"
   */
  constructor(dialog) {
    this._dialog = dialog;
    this._action = null;
    this._context = null;
    this._handlers = {};

    this._listener = (e) => this.handleSelection(e.detail.contentMedium);
    this._dialog?.addEventListener("content_medium:selected", this._listener);
  }

  /**
   * Register a handler for a named action.
   * @param {string} action - e.g. "fabric:add", "segment:replace"
   * @param {(contentMedium: object, context?: any) => void|Promise<void>} handler
   */
  on(action, handler) {
    this._handlers[action] = handler;
  }

  /**
   * Open the media library with an explicit intent.
   * @param {string} action - which handler to invoke on selection
   * @param {any} [context] - extra data passed to the handler (e.g. { index: 3 })
   */
  open(action, context = null) {
    this._action = action;
    this._context = context;
    window.openDialog(this._dialog.id);
  }

  /**
   * Called when a media is selected from the library.
   * Dispatches to the handler registered for the current action.
   */
  async handleSelection(contentMedium) {
    const action = this._action;
    const context = this._context;
    this._action = null;
    this._context = null;

    // Normalize: ensure cm.url is always set
    if (!contentMedium.url && contentMedium.serialized_file?.url) {
      contentMedium.url = contentMedium.serialized_file.url;
    }
    if (!contentMedium.url) return;

    const handler = this._handlers[action];
    if (handler) {
      await handler(contentMedium, context);
    }
  }

  /**
   * Remove the event listener. Call this on controller disconnect.
   */
  dispose() {
    this._dialog?.removeEventListener("content_medium:selected", this._listener);
  }
}
